import { useEffect, useMemo, useState } from "react";
import { endOfMonth, format } from "date-fns";
import { apiError } from "@/lib/api";
import { useSource } from "@/context/SourceContext";
import { currentMonth, monthLabel } from "@/lib/format";
import { dayLabel, useFilters } from "@/lib/filters";
import { FilterBar } from "@/components/filters/FilterBar";
import { KpiCards } from "@/components/dashboard/KpiCards";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { TrendTable } from "@/components/dashboard/TrendTable";
import { ErpSalesTrend, OutstandingCard, Toggle } from "@/components/dashboard/ErpWidgets";
import { PageHeader } from "@/components/PageHeader";

export default function DashboardPage() {
  const [range, setRange] = useState("12m");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const src = useSource();
  const month = currentMonth();
  const fl = useFilters(useMemo(() => [`${month}-01`, format(endOfMonth(new Date(`${month}-01T00:00:00`)), "yyyy-MM-dd")], [month]));

  useEffect(() => {
    src.get(`/dashboard?${fl.query({ range })}`).then((r) => { setData(r.data); setError(""); }).catch((e) => setError(apiError(e)));
  }, [src, fl.key, range]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="text-red-600" data-testid="dashboard-error">{error}</p>;
  const period = fl.dated ? `${dayLabel(fl.range[0])} – ${dayLabel(fl.range[1])}` : monthLabel(month);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader brand eyebrow="Overview" title="Dashboard"
        subtitle={data ? `${period} · all amounts in Omani Rial` : "Loading…"} />
      <FilterBar fl={fl} periodLabel={monthLabel(month)} allHistory={data && [`${data.months[data.months.length - 1]}-01`, data.today]} />
      <KpiCards data={data} />
      <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-end"><Toggle value={range} onChange={setRange} testid="trend-range-toggle" options={[["12m", "12 months"], ["all", "All history"]]} /></div>
            <TrendChart trend={data?.trend} />
          </div>
          {src.visible("sales") && <ErpSalesTrend month={data?.current_month || month} />}
        </div>
        <div className="space-y-6">
          {src.visible("outstanding") && <OutstandingCard />}
          {data?.current?.gp_note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="dashboard-gp-note">{data.current.gp_note}: {data.current.gp_missing_months.join(", ")}</p>}
          {src.note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="dashboard-excluded-note">{src.note}</p>}
        </div>
      </div>
      <TrendTable trend={data?.trend} />
    </div>
  );
}
